// hooks/useSpeechInput.ts
"use client";
import { useEffect, useCallback, useRef, useState } from "react";

export type MicPermission = "granted" | "denied" | "prompt" | "unknown";
export type SpeechStatus = "idle" | "requesting" | "listening" | "error";

interface UseSpeechInputOptions {
  onTranscript: (value: string) => void;
  lang?: string;
}

const useSpeechInput = ({
  onTranscript,
  lang = "tr-TR",
}: UseSpeechInputOptions) => {
  const onTranscriptRef = useRef(onTranscript);
  const recognitionRef = useRef<EventTarget | null>(null);
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);
  const accumulatedRef = useRef("");

  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [micPermission, setMicPermission] = useState<MicPermission>("unknown");
  const [isRequesting, setIsRequesting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");

  useEffect(() => {
    onTranscriptRef.current = onTranscript;
  }, [onTranscript]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setIsSupported(false);
      return;
    }

    setIsSupported(true);

    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "microphone" as PermissionName })
        .then((result) => {
          setMicPermission(result.state as MicPermission);
          result.onchange = () =>
            setMicPermission(result.state as MicPermission);
        })
        .catch(() => setMicPermission("unknown"));
    }

    const recognition = new SR() as EventTarget & {
      continuous: boolean;
      interimResults: boolean;
      lang: string;
      maxAlternatives: number;
      start(): void;
      stop(): void;
      abort(): void;
      onstart: (() => void) | null;
      onend: (() => void) | null;
      onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
      onresult: ((e: SpeechRecognitionEvent) => void) | null;
      onaudiostart: (() => void) | null;
      onaudioend: (() => void) | null;
      onspeechstart: (() => void) | null;
      onspeechend: (() => void) | null;
    };

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setHasError(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript("");
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = "";
      let final = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;

        if (result.isFinal) {
          final += text;
        } else {
          interim += text;
        }
      }

      if (final) {
        accumulatedRef.current += final;
        onTranscriptRef.current(accumulatedRef.current);

        if (silenceTimer.current) clearTimeout(silenceTimer.current);
        silenceTimer.current = setTimeout(() => {
          recognition.stop();
        }, 1500);
      }

      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === "not-allowed") {
        setMicPermission("denied");
        setHasError(true);
        setIsListening(false);
      } else if (event.error === "aborted") {
        // kullanıcı durdurdu, ignore
      } else {
        setHasError(true);
        setIsListening(false);
      }
      setInterimTranscript("");
    };

    recognitionRef.current = recognition;

    return () => {
      if (silenceTimer.current) clearTimeout(silenceTimer.current);
      recognition.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const status: SpeechStatus = hasError
    ? "error"
    : isListening
      ? "listening"
      : isRequesting
        ? "requesting"
        : "idle";

  const start = useCallback(async () => {
    const recognition = recognitionRef.current as { start(): void } | null;
    if (!recognition || isListening) return;

    setIsRequesting(true);
    setHasError(false);
    accumulatedRef.current = "";
    setInterimTranscript("");

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicPermission("granted");
    } catch {
      setMicPermission("denied");
      setHasError(true);
      setIsRequesting(false);
      return;
    }

    try {
      recognition.start();
    } catch {
      setHasError(true);
    } finally {
      setIsRequesting(false);
    }
  }, [isListening]);

  const stop = useCallback(() => {
    if (silenceTimer.current) clearTimeout(silenceTimer.current);
    setHasError(false);
    accumulatedRef.current = "";
    setInterimTranscript("");
    const recognition = recognitionRef.current as { stop(): void } | null;
    recognition?.stop();
  }, []);

  const toggle = useCallback(() => {
    if (isListening) stop();
    else start();
  }, [isListening, start, stop]);

  return {
    isListening,
    isSupported,
    micPermission,
    status,
    interimTranscript,
    toggle,
    stop,
    reset: useCallback(() => {
      accumulatedRef.current = "";
      setInterimTranscript("");
    }, []),
  };
};

export default useSpeechInput;
